
import React, { useState, useCallback, useRef, useEffect } from 'react';
import ReactFlow, {
    ReactFlowProvider,
    addEdge,
    useNodesState,
    useEdgesState,
    Controls,
    Background,
    MiniMap
} from 'reactflow';
import 'reactflow/dist/style.css';
import { DataModelSidebar } from './DataModelSidebar';
import DataModelInspector from './DataModelInspector';
// import TableNode from './nodes/TableNode';
import TableNode from '@/components/datamodels/TableNode';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Save, Play, Maximize, Minimize } from 'lucide-react';
import { useAuth } from '@/context/useAuth';
import { getDataModel, createDataModel, updateDataModel, refreshDataModel, getRefreshStatus, DataModel } from '@/api/datamodels';
import { toast } from 'sonner';
import { useTheme } from '@/context/ThemeContext';
// @ts-ignore
import { ConfirmModal } from '../../components/Common.jsx';

const nodeTypes = {
    table: TableNode,
};

interface DataModelDesignerProps {
    modelId: string | null;
    onBack: () => void;
    onSaveSuccess?: (model: DataModel) => void;
    fromFlow?: boolean;  // Hide storage config when true
}

export const DataModelDesigner: React.FC<DataModelDesignerProps> = ({ modelId, onBack, onSaveSuccess, fromFlow = false }) => {
    const reactFlowWrapper = useRef<HTMLDivElement>(null);
    const [nodes, setNodes, onNodesChange] = useNodesState([]);
    const [edges, setEdges, onEdgesChange] = useEdgesState([]);
    const [reactFlowInstance, setReactFlowInstance] = useState<any>(null);
    const { theme } = useTheme();

    // Unsaved Changes State
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [showExitWarning, setShowExitWarning] = useState(false);
    const [isFullScreen, setIsFullScreen] = useState(false);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [tableStats, setTableStats] = useState<any[]>([]);

    // Wrapped Change Handlers
    const onNodesChangeWrapped = useCallback((changes: any) => {
        onNodesChange(changes);
        if (changes.some((c: any) => c.type !== 'select')) {
            setHasUnsavedChanges(true);
        }
    }, [onNodesChange]);

    const onEdgesChangeWrapped = useCallback((changes: any) => {
        onEdgesChange(changes);
        if (changes.some((c: any) => c.type !== 'select')) {
            setHasUnsavedChanges(true);
        }
    }, [onEdgesChange]);

    // Inspector state
    const [selectedElement, setSelectedElement] = useState<any>(null);
    const [measures, setMeasures] = useState<any[]>([]);
    const { token } = useAuth();

    const [modelName, setModelName] = useState("New Data Model");
    const [currentModelId, setCurrentModelId] = useState<string | null>(modelId || null);
    const [isSaving, setIsSaving] = useState(false);

    // Sync with prop change from parent
    useEffect(() => {
        if (modelId) {
            setCurrentModelId(modelId);
        }
    }, [modelId]);

    // Storage configuration
    const [storageMode, setStorageMode] = useState("IMPORT");
    const [dataLayer, setDataLayer] = useState("BRONZE");
    const [refreshSchedule, setRefreshSchedule] = useState("MANUAL");
    const [selectedConnectionId, setSelectedConnectionId] = useState<string>("");

    // History for basic undo (store last 10 states)
    const [history, setHistory] = useState<Array<{ nodes: any[], edges: any[] }>>([]);
    const [historyIndex, setHistoryIndex] = useState(-1);

    // Helpers defined before use
    const onColumnsLoaded = useCallback((nodeId: string, columns: any[]) => {
        setNodes((nds) => nds.map((n) => {
            if (n.id === nodeId) {
                if (JSON.stringify(n.data.columns) !== JSON.stringify(columns)) {
                    return { ...n, data: { ...n.data, columns } };
                }
            }
            return n;
        }));
    }, [setNodes]);

    const onColumnSelect = useCallback((nodeId: string, column: any) => {
        setNodes(nds => nds.map(n => ({
            ...n,
            data: { ...n.data, selectedColumn: n.id === nodeId ? column.name : null }
        })));

        // We need to find the node to update selectedElement
        // But since state updates are async, we use the current 'nodes' or just trust the handler logic
        // For simplicity, we just look it up in the current 'nodes' state, which might be slightly stale but acceptable for selection
        // Better: Pass the full node data if available, but here we construct it.
        setNodes(currentNodes => {
            const node = currentNodes.find(n => n.id === nodeId);
            if (node) {
                setSelectedElement({ ...node, selectedColumn: column, data: { ...node.data, selectedColumn: column.name } });
            }
            return currentNodes;
        });
    }, [setNodes]);

    // Save state to history
    const saveToHistory = useCallback(() => {
        setHistory(prev => {
            const newHistory = prev.slice(0, historyIndex + 1);
            newHistory.push({ nodes: [...nodes], edges: [...edges] });
            // Keep only last 10 states
            if (newHistory.length > 10) newHistory.shift();
            return newHistory;
        });
        setHistoryIndex(prev => Math.min(prev + 1, 9));
    }, [nodes, edges, historyIndex]);

    // Undo function
    const handleUndo = useCallback(() => {
        if (historyIndex > 0) {
            const prevState = history[historyIndex - 1];
            setNodes(prevState.nodes);
            setEdges(prevState.edges);
            setHistoryIndex(prev => prev - 1);
        }
    }, [history, historyIndex, setNodes, setEdges]);

    const onDeleteElement = useCallback((id: string) => {
        saveToHistory();
        if (selectedElement?.source) {
            setEdges((eds) => eds.filter(e => e.id !== id));
        } else {
            setNodes((nds) => nds.filter(n => n.id !== id));
        }
        setSelectedElement(null);
        setHasUnsavedChanges(true);
    }, [selectedElement, setEdges, setNodes, saveToHistory]);

    const onUpdateElement = (id: string, updatedData: any) => {
        if (!selectedElement) return;

        if (selectedElement.source) {
            // Updating Edge
            setEdges((eds) => eds.map((e) => {
                if (e.id === id) {
                    return {
                        ...e,
                        label: updatedData.joinType, // Visual label
                        data: { ...e.data, ...updatedData } // Persisted data
                    };
                }
                return e;
            }));
            // Update local selection
            setSelectedElement((prev) => ({ ...prev, data: updatedData, label: updatedData.joinType }));
        } else {
            // Updating Node
            setNodes((nds) => nds.map((n) => {
                if (n.id === id) {
                    return { ...n, data: { ...n.data, ...updatedData } };
                }
                return n;
            }));
            // Update local selection
            setSelectedElement((prev) => ({ ...prev, data: { ...prev.data, ...updatedData } }));
        }
    };

    // Load Model
    useEffect(() => {
        if (token && currentModelId) {
            loadModel(currentModelId);
            checkRefreshStatus(currentModelId);
        }
    }, [token, currentModelId]);

    // Keyboard shortcuts moved below onSave

    const checkRefreshStatus = async (id: string) => {
        try {
            const status = await getRefreshStatus(id, token!);
            if (status.refresh_status === 'RUNNING') {
                setIsRefreshing(true);
            }
            if (status.table_stats) {
                setTableStats(status.table_stats);
            }
        } catch (e) {
            console.error("Failed to check refresh status", e);
        }
    };

    // Polling for refresh status
    useEffect(() => {
        let interval: NodeJS.Timeout;
        if (isRefreshing && currentModelId && token) {
            interval = setInterval(async () => {
                try {
                    const status = await getRefreshStatus(currentModelId, token);
                    if (status.table_stats) {
                        setTableStats(status.table_stats);
                    }
                    if (status.refresh_status !== 'RUNNING') {
                        setIsRefreshing(false);
                        if (status.refresh_status === 'SUCCESS') {
                            toast.success("Model extraction completed successfully");
                        } else if (status.refresh_status === 'FAILED') {
                            toast.error("Model extraction failed");
                        }
                    }
                } catch (e) {
                    console.error("Failed to poll refresh status", e);
                }
            }, 3000);
        }
        return () => clearInterval(interval);
    }, [isRefreshing, currentModelId, token]);

    const loadModel = async (id: string) => {
        try {
            const model: DataModel = await getDataModel(id, token!);
            setModelName(model.name);
            setStorageMode(model.storage_mode || "IMPORT");
            setDataLayer(model.data_layer || "BRONZE");
            setRefreshSchedule(model.refresh_schedule || "MANUAL");

            // Load selected connection if saved
            if (model.connection_id) {
                setSelectedConnectionId(model.connection_id);
            }

            if (model.model_definition?.tables) {
                const loadedNodes = model.model_definition.tables.map(t => ({
                    id: t.id!,
                    type: 'table',
                    position: { x: t.position_x || 0, y: t.position_y || 0 },
                    data: {
                        label: t.name,
                        source_table: t.source_table_name,
                        type: t.type,
                        source_connection_id: t.source_connection_id,
                        columns: t.columns || [],
                        onColumnsLoaded,
                        onColumnSelect
                    }
                }));
                setNodes(loadedNodes);
            }

            if (model.model_definition?.relationships) {
                const loadedEdges = model.model_definition.relationships.map(r => {
                    // Parse join condition to extract column names
                    // Format: "alias1.col1 = alias2.col2"
                    let sourceHandle: string | undefined;
                    let targetHandle: string | undefined;

                    if (r.join_condition && r.join_condition !== '1=1') {
                        // Simple regex to extract columns from "table.column = table.column"
                        const match = r.join_condition.match(/(\w+)\.(\w+)\s*=\s*(\w+)\.(\w+)/);
                        if (match) {
                            const [, , sourceCol, , targetCol] = match;
                            sourceHandle = `source-${sourceCol}`;
                            targetHandle = `target-${targetCol}`;
                        }
                    }

                    return {
                        id: r.id!,
                        source: r.from_table_id,
                        target: r.to_table_id,
                        sourceHandle,  // Restore handle for visual connection
                        targetHandle,  // Restore handle for visual connection
                        label: r.join_type,
                        animated: true,
                        data: { joinType: r.join_type, joinCondition: r.join_condition }
                    };
                });
                setEdges(loadedEdges);
            }

            if (model.model_definition?.measures) {
                setMeasures(model.model_definition.measures);
            }

        } catch (e) {
            toast.error("Failed to load model");
        }
    };

    const onConnect = useCallback((params) => {
        // console.log('[onConnect] Connection params:', params);

        saveToHistory();
        // Check if connection is column-to-column
        let joinCondition = "1=1";
        let joinType = "INNER"; // Default

        if (params.sourceHandle && params.targetHandle) {
            // Handles are ID'd as "source-{colName}" and "target-{colName}"
            const sourceCol = params.sourceHandle.replace('source-', '');
            const targetCol = params.targetHandle.replace('target-', '');

            // console.log('[onConnect] Column connection:', { sourceCol, targetCol });

            // Find nodes to get table aliases
            const sourceNode = nodes.find(n => n.id === params.source);
            const targetNode = nodes.find(n => n.id === params.target);

            console.log('[onConnect] Nodes:', {
                sourceNode: sourceNode?.data,
                targetNode: targetNode?.data
            });

            if (sourceNode && targetNode && sourceCol && targetCol) {
                const sourceAlias = sourceNode.data.label || sourceNode.data.source_table;
                const targetAlias = targetNode.data.label || targetNode.data.source_table;
                joinCondition = `${sourceAlias}.${sourceCol} = ${targetAlias}.${targetCol}`;

                // console.log('[onConnect] Generated join condition:', joinCondition);
            }
        } else {
            console.log('[onConnect] No handles - table-level connection');
        }

        const newEdge = {
            ...params,
            type: 'default',
            animated: true,
            label: joinType,
            data: { joinType, joinCondition }
        };

        // console.log('[onConnect] Creating edge:', newEdge);
        setEdges((eds) => addEdge(newEdge, eds));
        setHasUnsavedChanges(true);
    }, [nodes, setEdges]);

    const onDragOver = useCallback((event) => {
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
    }, []);

    const onDrop = useCallback(
        (event) => {
            event.preventDefault();

            saveToHistory();
            const type = event.dataTransfer.getData('application/reactflow');
            const payloadStr = event.dataTransfer.getData('application/payload');
            const payload = payloadStr ? JSON.parse(payloadStr) : {};

            if (typeof type === 'undefined' || !type) {
                return;
            }

            const position = reactFlowInstance.screenToFlowPosition({
                x: event.clientX,
                y: event.clientY,
            });

            const newNode = {
                id: `node_${Date.now()}`,
                type,
                position,
                data: {
                    label: payload.name,
                    source_table: payload.name,
                    type: 'PHYSICAL',
                    source_connection_id: payload.source_connection_id,
                    ...payload,
                    onColumnsLoaded,
                    onColumnSelect
                },
            };

            setNodes((nds) => nds.concat(newNode));
            setHasUnsavedChanges(true);
        },
        [reactFlowInstance, setNodes, onColumnsLoaded, onColumnSelect]
    );

    const onNodeClick = useCallback((event, node) => {
        setSelectedElement(node);
    }, []);

    const onEdgeClick = useCallback((event, edge) => {
        // Normalize edge data for inspector
        const edgeData = {
            ...edge,
            data: {
                joinType: edge.label || 'INNER',
                joinCondition: edge.data?.joinCondition || '1=1'
            }
        };
        setSelectedElement(edgeData);
    }, []);

    const onPaneClick = useCallback(() => {
        setSelectedElement(null);
    }, []);

    const handleBack = () => {
        if (hasUnsavedChanges) {
            setShowExitWarning(true);
        } else {
            onBack();
        }
    };

    const onSave = async () => {
        setIsSaving(true);
        try {
            // Construct Payload
            const tables = nodes.map(n => ({
                id: n.id,  // CRITICAL: Must save node ID for loading!
                name: n.data.label,  // Alias for SQL
                type: n.data.type || 'PHYSICAL',
                source_table_name: n.data.source_table,
                source_connection_id: n.data.source_connection_id,
                position_x: Math.round(n.position.x),
                position_y: Math.round(n.position.y),
                columns: n.data.columns // Save columns metadata
            }));


            const relationships = edges.map(e => ({
                id: e.id && e.id.startsWith("reactflow__") ? undefined : e.id,
                from_table_id: e.source,
                to_table_id: e.target,
                join_type: e.data?.joinType || "INNER",
                join_condition: e.data?.joinCondition || "1=1"
            }));

            const payload: DataModel = {
                name: modelName,
                connection_id: selectedConnectionId, // Save selected connection
                model_definition: {
                    tables: tables,
                    relationships: relationships,
                    measures: measures
                },
                storage_mode: storageMode,
                data_layer: dataLayer,
                refresh_schedule: refreshSchedule
            };

            console.log('[onSave] Model ID:', currentModelId);
            console.log('[onSave] Payload:', payload);

            if (currentModelId) {
                console.log('[onSave] Updating existing model...');
                const updatedModel = await updateDataModel(currentModelId, payload, token!);
                toast.success("Model saved");

                // Update internal state with new version ID
                if (updatedModel?.id) {
                    setCurrentModelId(updatedModel.id);
                }


                setHasUnsavedChanges(false);

                if (onSaveSuccess) onSaveSuccess(updatedModel);
            } else {
                console.log('[onSave] Creating new model...');
                const newModel = await createDataModel(payload, token!);
                toast.success("Model created");

                // Update internal state with new ID
                if (newModel?.id) {
                    setCurrentModelId(newModel.id);
                }


                setHasUnsavedChanges(false);

                if (onSaveSuccess) onSaveSuccess(newModel);
            }
        } catch (e) {
            console.error('[onSave] Error:', e);
            toast.error("Failed to save");
        } finally {
            setIsSaving(false);
        }
    };

    // Keyboard shortcuts
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            // Ctrl+S to save
            if ((e.ctrlKey || e.metaKey) && e.key === 's') {
                e.preventDefault();
                onSave();
            }
            // Ctrl+Z to undo
            if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
                e.preventDefault();
                handleUndo();
            }
            // Delete to remove selected element
            if (e.key === 'Delete' && selectedElement) {
                e.preventDefault();
                onDeleteElement(selectedElement.id);
            }
            // Escape to deselect
            if (e.key === 'Escape') {
                setSelectedElement(null);
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [selectedElement, onDeleteElement, handleUndo, onSave]);

    // Wrapper for property updates
    const handleUpdateElement = (id: string, data: any) => {
        onUpdateElement(id, data);
        setHasUnsavedChanges(true);
    };

    const handleDeleteElement = (id: string) => {
        onDeleteElement(id);
        setHasUnsavedChanges(true);
    };

    return (
        <div className={`flex flex-col ${isFullScreen ? 'fixed inset-0 z-50 h-screen w-screen bg-white dark:bg-slate-900' : 'h-full'}`}>
            {/* Toolbar */}
            <div className="h-14 border-b border-gray-200 dark:border-slate-800 bg-white dark:bg-slate-900 flex items-center justify-between px-4">
                <div className="flex items-center gap-4">
                    <Button variant="ghost" size="sm" onClick={handleBack} className="text-gray-600 dark:text-slate-400 dark:hover:text-slate-200">
                        <ArrowLeft className="w-4 h-4 mr-2" /> Back
                    </Button>
                    <input
                        value={modelName}
                        onChange={e => { setModelName(e.target.value); setHasUnsavedChanges(true); }}
                        className="text-lg font-semibold bg-transparent border-none focus:ring-0 text-gray-900 dark:text-slate-100 placeholder-gray-400 dark:placeholder-slate-500"
                    />
                    <div className="text-xs text-gray-400 border-l pl-3 hidden lg:block">
                        <kbd className="px-1.5 py-0.5 bg-gray-100 dark:bg-slate-800 rounded border border-gray-300 dark:border-slate-700">Ctrl+S</kbd> Save •
                        <kbd className="px-1.5 py-0.5 bg-gray-100 dark:bg-slate-800 rounded border border-gray-300 dark:border-slate-700 ml-1">Ctrl+Z</kbd> Undo •
                        <kbd className="px-1.5 py-0.5 bg-gray-100 dark:bg-slate-800 rounded border border-gray-300 dark:border-slate-700 ml-1">Del</kbd> Delete
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm"
                        disabled={isRefreshing}
                        onClick={async () => {
                            if (!modelId) {
                                toast.error("Please save the model first");
                                return;
                            }
                            try {
                                setIsRefreshing(true);
                                await refreshDataModel(modelId, token!);
                                toast.success("Model extraction queued");
                            } catch (e) {
                                setIsRefreshing(false);
                                toast.error("Failed to queue model extraction");
                            }
                        }}
                        className="dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700 dark:hover:bg-slate-700 disabled:opacity-50"
                    >
                        <Play className={`w-4 h-4 mr-2 ${isRefreshing ? 'animate-pulse text-purple-500' : ''}`} />
                        {isRefreshing ? 'Extracting...' : 'Build/Refresh'}
                    </Button>
                    <Button size="sm" onClick={onSave} disabled={isSaving} className="bg-purple-600 hover:bg-purple-700 text-white dark:bg-purple-600 dark:hover:bg-purple-500">
                        <Save className="w-4 h-4 mr-2" /> Save
                    </Button>
                    <button
                        onClick={() => setIsFullScreen(!isFullScreen)}
                        className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                        title={isFullScreen ? "Exit Full Screen" : "Enter Full Screen"}
                    >
                        {isFullScreen ? <Minimize className="w-4 h-4" /> : <Maximize className="w-4 h-4" />}
                    </button>
                </div>
            </div>

            {/* Editor Area */}
            <div className="flex-1 flex overflow-hidden">
                <DataModelSidebar
                    selectedConnection={selectedConnectionId}
                    onConnectionChange={setSelectedConnectionId}
                />
                <div className="flex-1 h-full relative" ref={reactFlowWrapper}>
                    <ReactFlowProvider>
                        <ReactFlow
                            nodes={nodes}
                            edges={edges}
                            onNodesChange={onNodesChangeWrapped}
                            onEdgesChange={onEdgesChangeWrapped}
                            onConnect={onConnect}
                            onInit={setReactFlowInstance}
                            onDrop={onDrop}
                            onDragOver={onDragOver}
                            onNodeClick={onNodeClick}
                            onEdgeClick={onEdgeClick}
                            onPaneClick={onPaneClick}
                            nodeTypes={nodeTypes}
                            fitView
                        >
                            <Background color={theme.mode === 'dark' ? '#334155' : '#aaa'} gap={16} />
                            <Controls className="dark:bg-slate-800 dark:border-slate-700 dark:text-slate-200" />
                            <MiniMap className="dark:bg-slate-900 dark:border-slate-700" maskColor={theme.mode === 'dark' ? 'rgba(30, 41, 59, 0.7)' : 'rgba(240, 240, 240, 0.6)'} />
                        </ReactFlow>
                    </ReactFlowProvider>
                </div>
                {/* Always show inspector if selected or generally available for model properties */}
                <DataModelInspector
                    selectedElement={selectedElement}
                    modelName={modelName}
                    onUpdateModelName={setModelName}
                    measures={measures}
                    onUpdateMeasures={setMeasures}
                    onUpdate={handleUpdateElement}
                    onDelete={handleDeleteElement}
                    onClose={() => setSelectedElement(null)}
                    nodes={nodes}
                    storageMode={storageMode}
                    onUpdateStorageMode={(val) => { setStorageMode(val); setHasUnsavedChanges(true); }}
                    dataLayer={dataLayer}
                    onUpdateDataLayer={(val) => { setDataLayer(val); setHasUnsavedChanges(true); }}
                    refreshSchedule={refreshSchedule}
                    onUpdateRefreshSchedule={(val) => { setRefreshSchedule(val); setHasUnsavedChanges(true); }}
                    fromFlow={fromFlow}
                    dataSourceId={selectedConnectionId}
                    tableStats={selectedElement?.data?.source_table ? tableStats?.find(ts => ts.table_name === selectedElement.data.source_table) : null}
                />
            </div>
            <ConfirmModal
                zIndex={1300}
                isOpen={showExitWarning}
                onClose={() => setShowExitWarning(false)}
                onConfirm={() => {
                    setShowExitWarning(false);
                    onBack();
                }}
                title="Unsaved Changes"
            >
                You have unsaved changes. Are you sure you want to go back?
            </ConfirmModal>
        </div >
    );
};
